/**
 * Vanta Manage API payloads → SourceRecord (spec section 6). Raw status is always retained; a
 * missing or unrecognised status is UNKNOWN, never PASS. Test entities are children of tests;
 * monitored computers are linked to people through `device_owner` relations.
 */
import type { RecordKey } from '../../ids.js';
import { canonicalRecordKey } from '../../ids.js';
import type { Classification, Link, SourceRecord } from '../../providers/records.js';
import type {
  VantaDocument,
  VantaDocumentStatus,
  VantaEntityStatus,
  VantaFramework,
  VantaFrameworkControl,
  VantaIntegration,
  VantaMonitoredComputer,
  VantaPerson,
  VantaPolicy,
  VantaPolicyStatus,
  VantaSeverity,
  VantaTest,
  VantaTestEntity,
  VantaTestStatus,
  VantaVendor,
  VantaVulnerability,
} from '../../providers/vanta.js';
import { VANTA_EMPLOYMENT_STATUSES, VANTA_REAL_EMPLOYMENT_STATUSES, vantaRecordUrl, vantaTenantFromBrowserUrl } from '../../providers/vanta.js';
import type { SeverityLevel, StandardStatus } from '../../status.js';
import { applyClassificationRules, VANTA_CLASSIFICATION_RULES } from './classificationRules.js';
import {
  browserLink,
  buildRecord,
  dedupeAndSort,
  emptyResult,
  linkChildren,
  makeKey,
  safeLink,
  type NormalizeContext,
  type NormalizeResult,
  type NormalizeWarning,
  type ParentChildRelation,
  controlTitle,
} from './common.js';

// ---------------------------------------------------------------------------------------------
// Status mapping
// ---------------------------------------------------------------------------------------------

const TEST_STATUS: Record<VantaTestStatus, StandardStatus> = {
  NEEDS_ATTENTION: 'FAIL',
  OK: 'PASS',
  DEACTIVATED: 'DEACTIVATED',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  IN_PROGRESS: 'NEEDS_ATTENTION',
  INVALID: 'UNKNOWN',
};

export function mapVantaTestStatus(raw: string | null | undefined): StandardStatus {
  if (!raw) return 'UNKNOWN';
  return (TEST_STATUS as Record<string, StandardStatus>)[raw] ?? 'UNKNOWN';
}

const ENTITY_STATUS: Record<VantaEntityStatus, StandardStatus> = {
  FAILING: 'FAIL',
  PASSING: 'PASS',
  DEACTIVATED: 'DEACTIVATED',
  IGNORED: 'NOT_APPLICABLE',
};

export function mapVantaEntityStatus(raw: string | null | undefined): StandardStatus {
  if (!raw) return 'UNKNOWN';
  return (ENTITY_STATUS as Record<string, StandardStatus>)[raw] ?? 'UNKNOWN';
}

const DOCUMENT_STATUS: Record<VantaDocumentStatus, StandardStatus> = {
  OK: 'PASS',
  NEEDS_ATTENTION: 'FAIL',
  DUE_SOON: 'DUE_SOON',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  NOT_RELEVANT: 'NOT_APPLICABLE',
  // real-providers change: verified `uploadStatus` values (PROVIDER_API_NOTES V8).
  'Needs document': 'NEEDS_ATTENTION',
  'Needs update': 'NEEDS_ATTENTION',
  'Not relevant': 'NOT_APPLICABLE',
};

export function mapVantaDocumentStatus(raw: string | null | undefined): StandardStatus {
  if (!raw) return 'UNKNOWN';
  return (DOCUMENT_STATUS as Record<string, StandardStatus>)[raw] ?? 'UNKNOWN';
}

const POLICY_STATUS: Record<VantaPolicyStatus, StandardStatus> = {
  APPROVED: 'PASS',
  DRAFT: 'NEEDS_ATTENTION',
  NEEDS_APPROVAL: 'NEEDS_ATTENTION',
  EXPIRED: 'FAIL',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  // real-providers change: verified `status` values (PROVIDER_API_NOTES V9), refined by `latestVersion.status`.
  OK: 'PASS',
  NEEDS_REMEDIATION: 'NEEDS_ATTENTION',
};

/** real-providers change: `latestVersion.status` refinement for the verified `OK`/`NEEDS_REMEDIATION` policy statuses. */
const POLICY_VERSION_STATUS: Record<string, StandardStatus> = {
  RENEW_SOON: 'DUE_SOON',
  EXPIRED: 'FAIL',
  DRAFT: 'NEEDS_ATTENTION',
  PENDING_APPROVAL: 'NEEDS_ATTENTION',
  NOT_STARTED: 'NEEDS_ATTENTION',
};

export function mapVantaPolicyStatus(raw: string | null | undefined, versionStatus?: string | null): StandardStatus {
  if (!raw) return 'UNKNOWN';
  const base = (POLICY_STATUS as Record<string, StandardStatus>)[raw] ?? 'UNKNOWN';
  if ((raw === 'OK' || raw === 'NEEDS_REMEDIATION') && versionStatus && POLICY_VERSION_STATUS[versionStatus]) return POLICY_VERSION_STATUS[versionStatus]!;
  return base;
}

const SEVERITY: Record<VantaSeverity, SeverityLevel> = { CRITICAL: 'critical', HIGH: 'high', MEDIUM: 'medium', LOW: 'low' };

export function mapVantaSeverity(raw: string | null | undefined): SeverityLevel | null {
  if (!raw) return null;
  return (SEVERITY as Record<string, SeverityLevel>)[raw] ?? null;
}

export function mapVantaIntegrationStatus(raw: string | null | undefined): StandardStatus {
  switch (raw) {
    case 'CONNECTED':
      return 'PASS';
    case 'DISCONNECTED':
      return 'FAIL';
    case 'ERROR':
      return 'NEEDS_ATTENTION';
    case 'PENDING':
      return 'NEEDS_ATTENTION';
    // real-providers change: derived from the verified `connections[]` list (all connections disabled).
    case 'DISABLED':
      return 'DEACTIVATED';
    default:
      return 'UNKNOWN';
  }
}

// ---------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------

function classify(objectType: SourceRecord['objectType'], name: string, opts: { description?: string | null; category?: string | null; integrationIds?: readonly string[]; providerIsPolicy?: boolean | null } = {}): Classification {
  return applyClassificationRules(VANTA_CLASSIFICATION_RULES, { provider: 'vanta', objectType, name, ...opts }).classification;
}

function frameworkIdsOf(items: readonly { frameworkId: string }[] | undefined, derived: Iterable<string> = []): string[] {
  return [...new Set([...(items ?? []).map((f) => f.frameworkId), ...derived])].sort();
}

/**
 * The in-app deep link for a test or document (`app.vanta.com/c/<slug>/<tests|documents>/<id>`) when the
 * connection's browser URL names the tenant slug; null otherwise, so the caller falls back to the
 * provider-supplied URL or the plain browser-URL join.
 */
function vantaDeepLink(ctx: NormalizeContext, label: string, kind: 'test' | 'document', id: string): Link | null {
  const tenant = vantaTenantFromBrowserUrl(ctx.browserUrl);
  return tenant ? safeLink(label, vantaRecordUrl(tenant, kind, id), 'vanta', ctx) : null;
}

function pushLink(links: Link[], warnings: NormalizeWarning[], link: Link | null, url: string | null | undefined, objectType: SourceRecord['objectType'], sourceId: string): void {
  if (link) links.push(link);
  else if (url) warnings.push({ code: 'invalid_link', objectType, sourceId, message: 'link rejected: host not allowed or not https' });
}

// ---------------------------------------------------------------------------------------------
// Per-object normalizers
// ---------------------------------------------------------------------------------------------

export function normalizeVantaTest(test: VantaTest, ctx: NormalizeContext, warnings: NormalizeWarning[] = [], derivedFrameworkIds?: Iterable<string>): SourceRecord {
  const links: Link[] = [];
  const integrationIds = test.integrations.map((i) => i.integrationId);
  pushLink(links, warnings, vantaDeepLink(ctx, 'Open in Vanta', 'test', test.id) ?? safeLink('Open in Vanta', test.testUrl, 'vanta', ctx) ?? browserLink(ctx, 'vanta', 'Open in Vanta', `/tests/${encodeURIComponent(test.id)}`), test.testUrl, 'test', test.id);
  const deactivated = test.status === 'DEACTIVATED' || test.deactivatedStatusInfo?.isDeactivated === true;
  return buildRecord(ctx, {
    provider: 'vanta',
    objectType: 'test',
    sourceId: test.id,
    canonicalKey: canonicalRecordKey('vanta', 'test', test.id),
    title: test.name,
    description: test.description ?? null,
    rawStatus: test.status,
    status: deactivated ? 'DEACTIVATED' : mapVantaTestStatus(test.status),
    deactivated,
    classification: classify('test', test.name, { description: test.description, category: test.category, integrationIds }),
    frameworkIds: frameworkIdsOf(test.frameworks, derivedFrameworkIds),
    links,
    // real-providers change: `lastTestRunDate` is the verified run timestamp when no flip date exists.
    sourceUpdatedAt: test.latestFlipDate ?? test.lastTestRunDate ?? test.deactivatedStatusInfo?.lastUpdatedDate ?? null,
    remediation: test.remediation ?? null,
    raw: test,
  });
}

export function normalizeVantaTestEntity(entity: VantaTestEntity, ctx: NormalizeContext, warnings: NormalizeWarning[] = []): SourceRecord {
  const links: Link[] = [];
  pushLink(links, warnings, safeLink('Open entity', entity.entityUrl, 'vanta', ctx), entity.entityUrl, 'test_entity', entity.id);
  const deactivated = entity.status === 'DEACTIVATED';
  return buildRecord(ctx, {
    provider: 'vanta',
    objectType: 'test_entity',
    sourceId: entity.id,
    canonicalKey: null,
    title: entity.displayName,
    description: entity.failureDescription ?? null,
    rawStatus: entity.status,
    status: mapVantaEntityStatus(entity.status),
    deactivated,
    classification: { isPolicy: false, isInfrastructure: false, isEmployeeCompliance: false, isCompanyCompliance: false },
    parentKey: makeKey(ctx, 'vanta', 'test', entity.testId),
    links,
    sourceUpdatedAt: entity.firstFailingAt ?? null,
    raw: entity,
  });
}

export function normalizeVantaDocument(doc: VantaDocument, ctx: NormalizeContext, warnings: NormalizeWarning[] = [], derivedFrameworkIds?: Iterable<string>): SourceRecord {
  const links: Link[] = [];
  pushLink(links, warnings, vantaDeepLink(ctx, 'Open in Vanta', 'document', doc.id) ?? safeLink('Open document', doc.documentUrl, 'vanta', ctx) ?? browserLink(ctx, 'vanta', 'Open document', `/documents/${encodeURIComponent(doc.id)}`), doc.documentUrl, 'document', doc.id);
  return buildRecord(ctx, {
    provider: 'vanta',
    objectType: 'document',
    sourceId: doc.id,
    canonicalKey: null,
    title: doc.title,
    description: doc.description ?? null,
    rawStatus: doc.status,
    status: mapVantaDocumentStatus(doc.status),
    deactivated: doc.status === 'NOT_RELEVANT' || doc.status === 'Not relevant',
    classification: classify('document', doc.title, { description: doc.description, category: doc.category, providerIsPolicy: doc.isPolicy }),
    frameworkIds: frameworkIdsOf(doc.frameworks, derivedFrameworkIds),
    links,
    sourceUpdatedAt: doc.updatedAt ?? null,
    raw: doc,
  });
}

export function normalizeVantaPolicy(policy: VantaPolicy, ctx: NormalizeContext, warnings: NormalizeWarning[] = [], derivedFrameworkIds?: Iterable<string>): SourceRecord {
  const links: Link[] = [];
  pushLink(links, warnings, safeLink('Open policy', policy.policyUrl, 'vanta', ctx) ?? browserLink(ctx, 'vanta', 'Open policy', `/policies/${encodeURIComponent(policy.id)}`), policy.policyUrl, 'policy', policy.id);
  const accepted = policy.acceptanceCount ?? null;
  const required = policy.requiredAcceptanceCount ?? null;
  const acceptanceNote = accepted !== null && required !== null ? `Acceptance ${accepted}/${required}` : null;
  return buildRecord(ctx, {
    provider: 'vanta',
    objectType: 'policy',
    sourceId: policy.id,
    canonicalKey: null,
    title: policy.name,
    description: [policy.description ?? null, acceptanceNote].filter(Boolean).join('\n') || null,
    rawStatus: policy.status,
    status: mapVantaPolicyStatus(policy.status, policy.latestVersion?.status ?? null),
    deactivated: false,
    classification: classify('policy', policy.name),
    frameworkIds: frameworkIdsOf(policy.frameworks, derivedFrameworkIds),
    links,
    sourceUpdatedAt: policy.updatedAt ?? policy.approvedAt ?? null,
    raw: policy,
  });
}

export function normalizeVantaFramework(fw: VantaFramework, ctx: NormalizeContext, warnings: NormalizeWarning[] = []): SourceRecord {
  const links: Link[] = [];
  pushLink(links, warnings, safeLink('Open framework', fw.frameworkUrl, 'vanta', ctx) ?? browserLink(ctx, 'vanta', 'Open framework', `/frameworks/${encodeURIComponent(fw.id)}`), fw.frameworkUrl, 'framework', fw.id);
  const cs = fw.completionStatus;
  const complete = cs.controlsTotal !== null && cs.controlsPassing !== null && cs.controlsTotal > 0 && cs.controlsPassing >= cs.controlsTotal;
  return buildRecord(ctx, {
    provider: 'vanta',
    objectType: 'framework',
    sourceId: fw.id,
    canonicalKey: canonicalRecordKey('vanta', 'framework', fw.id),
    title: fw.name,
    description: fw.description ?? null,
    rawStatus: cs.controlsTotal === null ? null : `${cs.controlsPassing ?? '?'}/${cs.controlsTotal}`,
    status: cs.controlsTotal === null || cs.controlsPassing === null ? 'UNKNOWN' : complete ? 'PASS' : 'NEEDS_ATTENTION',
    deactivated: false,
    classification: { isPolicy: false, isInfrastructure: false, isEmployeeCompliance: false, isCompanyCompliance: true },
    frameworkIds: [fw.id],
    links,
    raw: fw,
  });
}

export function normalizeVantaFrameworkControl(control: VantaFrameworkControl, frameworkId: string | null, ctx: NormalizeContext): SourceRecord {
  const fwId = control.frameworkId ?? frameworkId;
  return buildRecord(ctx, {
    provider: 'vanta',
    objectType: 'control',
    sourceId: control.id,
    // Vanta framework-control ids are catalog identifiers (same across tenants), like test ids.
    canonicalKey: canonicalRecordKey('vanta', 'control', control.id),
    title: controlTitle(control.externalId ?? null, control.name),
    description: control.description ?? null,
    rawStatus: control.status ?? null,
    status: control.status ? mapVantaTestStatus(control.status) : 'UNKNOWN',
    deactivated: control.status === 'DEACTIVATED',
    classification: classify('control', control.name, { description: control.description }),
    frameworkIds: fwId ? [fwId] : [],
    raw: control,
  });
}

/** Sources control readiness from applicable linked evidence; an unknown child never passes. */
export function deriveVantaControlStatus(children: readonly StandardStatus[]): StandardStatus {
  const applicable = children.filter((status) => status !== 'DEACTIVATED' && status !== 'NOT_APPLICABLE');
  if (applicable.length === 0) return 'UNKNOWN';
  if (applicable.some((status) => status === 'FAIL' || status === 'NEEDS_ATTENTION')) return 'FAIL';
  return applicable.every((status) => status === 'PASS') ? 'PASS' : 'NEEDS_ATTENTION';
}

/** Ecosystem-qualified package identity when the provider gives one, else `source:name@version`. */
export function vantaPackageIdentifier(v: VantaVulnerability): string | null {
  if (v.packageIdentifier) return v.packageIdentifier;
  if (!v.packageName) return null;
  return `${v.source}:${v.packageName}${v.installedVersion ? `@${v.installedVersion}` : ''}`;
}

export function normalizeVantaVulnerability(v: VantaVulnerability, ctx: NormalizeContext, warnings: NormalizeWarning[] = []): SourceRecord {
  const links: Link[] = [];
  if (v.externalUrl) {
    // External advisories may live on any https host; only require https and no credentials.
    try {
      const u = new URL(v.externalUrl);
      if (u.protocol === 'https:' && !u.username && !u.password) links.push({ label: 'Advisory', url: v.externalUrl });
      else warnings.push({ code: 'invalid_link', objectType: 'vulnerability', sourceId: v.id, message: 'advisory link rejected' });
    } catch {
      warnings.push({ code: 'invalid_link', objectType: 'vulnerability', sourceId: v.id, message: 'advisory link unparsable' });
    }
  }
  // real-providers change: the verified API marks deactivation with a `deactivateMetadata` object.
  const deactivated = v.deactivatedAt != null || v.deactivateMetadata != null;
  const sourceId = `${v.id}|${v.assetId}|${vantaPackageIdentifier(v) ?? ''}`;
  return buildRecord(ctx, {
    provider: 'vanta',
    objectType: 'vulnerability',
    sourceId,
    canonicalKey: null,
    title: v.name,
    description: v.description ?? null,
    rawStatus: deactivated ? 'DEACTIVATED' : 'OPEN',
    status: deactivated ? 'DEACTIVATED' : 'FAIL',
    severity: mapVantaSeverity(v.severity),
    deactivated,
    classification: classify('vulnerability', v.name),
    links,
    sourceUpdatedAt: v.lastDetectedAt ?? v.firstDetectedAt,
    raw: v,
  });
}

/** real-providers change: legacy plus verified `employment.status` values; anything else maps to UNKNOWN. */
const KNOWN_VANTA_EMPLOYMENT: ReadonlySet<string> = new Set<string>([...VANTA_EMPLOYMENT_STATUSES, ...VANTA_REAL_EMPLOYMENT_STATUSES]);

export function normalizeVantaPerson(person: VantaPerson, ctx: NormalizeContext, warnings: NormalizeWarning[] = []): SourceRecord {
  const links: Link[] = [];
  const onboarding = person.tasksSummary?.onboardingUrl;
  pushLink(links, warnings, safeLink('Onboarding', onboarding, 'vanta', ctx), onboarding, 'person', person.id);
  const ts = person.tasksSummary ?? null;
  let status: StandardStatus = 'UNKNOWN';
  if (person.employmentStatus === 'FORMER') status = 'DEACTIVATED';
  else if (person.employmentStatus === 'OUT_OF_SCOPE' || person.employmentStatus === 'SERVICE_ACCOUNT' || person.employmentStatus === 'INACTIVE') status = 'NOT_APPLICABLE';
  // real-providers change: an employment status the contract does not list cannot derive a pass from the tasks.
  else if (!KNOWN_VANTA_EMPLOYMENT.has(person.employmentStatus ?? '')) status = 'UNKNOWN';
  // real-providers change: verified `tasksSummary.status` values (COMPLETE/DUE_SOON/NONE/OVERDUE/PAUSED/OFFBOARDING_*).
  else if (ts?.status === 'COMPLETE' || ts?.status === 'OFFBOARDING_COMPLETE') status = 'PASS';
  else if (ts?.status === 'OVERDUE' || ts?.status === 'INCOMPLETE' || ts?.status === 'OFFBOARDING_OVERDUE') status = 'FAIL';
  else if (ts?.status === 'DUE_SOON' || ts?.status === 'OFFBOARDING_DUE_SOON') status = 'DUE_SOON';
  else if (ts?.status === 'NOT_APPLICABLE' || ts?.status === 'NONE') status = 'NOT_APPLICABLE';
  return buildRecord(ctx, {
    provider: 'vanta',
    objectType: 'person',
    sourceId: person.id,
    canonicalKey: null,
    title: person.displayName,
    description: person.emailAddress,
    rawStatus: ts?.status ?? person.employmentStatus,
    status,
    deactivated: person.employmentStatus === 'FORMER',
    classification: { isPolicy: false, isInfrastructure: false, isEmployeeCompliance: true, isCompanyCompliance: false },
    links,
    sourceUpdatedAt: ts?.completionDate ?? person.startDate ?? null,
    raw: person,
  });
}

export function normalizeVantaComputer(computer: VantaMonitoredComputer, ctx: NormalizeContext): SourceRecord {
  const checks = [computer.agentInstalled, computer.passwordManager, computer.diskEncryption, computer.antivirus, computer.autoUpdates, computer.screenLock];
  const anyFalse = checks.some((c) => c === false);
  const allTrue = checks.every((c) => c === true);
  const anyKnown = checks.some((c) => typeof c === 'boolean');
  const inactive = computer.isActive === false;
  return buildRecord(ctx, {
    provider: 'vanta',
    objectType: 'computer',
    sourceId: computer.id,
    canonicalKey: null,
    title: computer.hostname ?? computer.serialNumber ?? computer.udid ?? computer.id,
    description: computer.osVersion ?? null,
    rawStatus: inactive ? 'INACTIVE' : anyKnown ? (anyFalse ? 'FAILING_CHECKS' : allTrue ? 'ALL_CHECKS_PASS' : 'PARTIAL_CHECKS') : null,
    status: inactive ? 'DEACTIVATED' : !anyKnown ? 'UNKNOWN' : anyFalse ? 'FAIL' : allTrue ? 'PASS' : 'UNKNOWN',
    deactivated: inactive,
    classification: { isPolicy: false, isInfrastructure: false, isEmployeeCompliance: true, isCompanyCompliance: false },
    sourceUpdatedAt: computer.lastCheckIn ?? null,
    raw: computer,
  });
}

export function normalizeVantaIntegration(integration: VantaIntegration, ctx: NormalizeContext): SourceRecord {
  return buildRecord(ctx, {
    provider: 'vanta',
    objectType: 'integration',
    sourceId: integration.id,
    // The integration kind (aws, okta, ...) is the catalog identity; `id` is the tenant connection.
    canonicalKey: integration.integrationId ? canonicalRecordKey('vanta', 'integration', integration.integrationId) : null,
    title: integration.displayName,
    description: integration.category ?? null,
    rawStatus: integration.connectionStatus,
    status: mapVantaIntegrationStatus(integration.connectionStatus),
    deactivated: false,
    classification: classify('integration', integration.displayName, { category: integration.category }),
    sourceUpdatedAt: integration.lastSyncedAt ?? integration.connectedAt ?? null,
    raw: integration,
  });
}

export function normalizeVantaVendor(vendor: VantaVendor, ctx: NormalizeContext, warnings: NormalizeWarning[] = []): SourceRecord {
  const links: Link[] = [];
  if (vendor.websiteUrl) {
    try {
      const u = new URL(vendor.websiteUrl);
      if (u.protocol === 'https:') links.push({ label: 'Website', url: vendor.websiteUrl });
      else warnings.push({ code: 'invalid_link', objectType: 'vendor', sourceId: vendor.id, message: 'vendor website is not https' });
    } catch {
      warnings.push({ code: 'invalid_link', objectType: 'vendor', sourceId: vendor.id, message: 'vendor website unparsable' });
    }
  }
  const risk = vendor.riskLevel ? mapVantaSeverity(vendor.riskLevel) : null;
  return buildRecord(ctx, {
    provider: 'vanta',
    objectType: 'vendor',
    sourceId: vendor.id,
    canonicalKey: null,
    title: vendor.name,
    description: vendor.description ?? null,
    rawStatus: vendor.status ?? null,
    // real-providers change: verified statuses MANAGED (PASS), IN_PROCUREMENT (NEEDS_ATTENTION), ARCHIVED (deactivated).
    status: vendor.status ? (/^(active|approved|reviewed|managed)$/i.test(vendor.status) ? 'PASS' : /(review|pending|overdue|procurement)/i.test(vendor.status) ? 'NEEDS_ATTENTION' : 'UNKNOWN') : 'UNKNOWN',
    severity: risk,
    deactivated: vendor.status ? /^(archived|offboarded|inactive)$/i.test(vendor.status) : false,
    classification: classify('vendor', vendor.name, { category: vendor.category }),
    links,
    sourceUpdatedAt: vendor.lastReviewedAt ?? null,
    raw: vendor,
  });
}

// ---------------------------------------------------------------------------------------------
// Payload-level normalization
// ---------------------------------------------------------------------------------------------

export interface VantaPayload {
  tests?: readonly VantaTest[];
  testEntities?: readonly VantaTestEntity[];
  documents?: readonly VantaDocument[];
  policies?: readonly VantaPolicy[];
  frameworks?: readonly VantaFramework[];
  /** Controls grouped by the framework they were fetched under. */
  frameworkControls?: readonly { frameworkId: string; controls: readonly VantaFrameworkControl[] }[];
  /** False when an optional child dataset could not be collected; inline/imported links omit it. */
  controlLinksComplete?: boolean;
  /** Membership from the provider's framework-filtered lists when controls omit their links. */
  frameworkMemberships?: readonly { frameworkId: string; testIds: readonly string[]; documentIds: readonly string[] }[];
  vulnerabilities?: readonly VantaVulnerability[];
  people?: readonly VantaPerson[];
  monitoredComputers?: readonly VantaMonitoredComputer[];
  integrations?: readonly VantaIntegration[];
  vendors?: readonly VantaVendor[];
}

/**
 * Normalize an entire Vanta collection. Relations returned separately cover links SourceRecord
 * cannot express with a single parentKey (control → tests/documents, computer → owner).
 */
export function normalizeVantaPayload(payload: VantaPayload, ctx: NormalizeContext): NormalizeResult {
  const result = emptyResult(VANTA_CLASSIFICATION_RULES.version);
  const { warnings } = result;
  const records: SourceRecord[] = [];
  const relations: ParentChildRelation[] = [];

  const testFrameworks = new Map<string, Set<string>>();
  const documentFrameworks = new Map<string, Set<string>>();
  const addMembership = (map: Map<string, Set<string>>, id: string, frameworkId: string) => {
    const ids = map.get(id) ?? new Set<string>();
    ids.add(frameworkId);
    map.set(id, ids);
  };
  for (const group of payload.frameworkControls ?? []) {
    for (const control of group.controls) {
      for (const id of control.testIds) addMembership(testFrameworks, id, group.frameworkId);
      for (const id of control.documentIds) addMembership(documentFrameworks, id, group.frameworkId);
    }
  }

  for (const group of payload.frameworkMemberships ?? []) {
    for (const id of group.testIds) addMembership(testFrameworks, id, group.frameworkId);
    for (const id of group.documentIds) addMembership(documentFrameworks, id, group.frameworkId);
  }

  for (const t of payload.tests ?? []) records.push(normalizeVantaTest(t, ctx, warnings, testFrameworks.get(t.id)));
  for (const e of payload.testEntities ?? []) records.push(normalizeVantaTestEntity(e, ctx, warnings));
  for (const d of payload.documents ?? []) records.push(normalizeVantaDocument(d, ctx, warnings, documentFrameworks.get(d.id)));
  for (const p of payload.policies ?? []) records.push(normalizeVantaPolicy(p, ctx, warnings, p.documentId ? documentFrameworks.get(p.documentId) : undefined));
  for (const f of payload.frameworks ?? []) records.push(normalizeVantaFramework(f, ctx, warnings));
  for (const group of payload.frameworkControls ?? []) {
    for (const c of group.controls) {
      const rec = normalizeVantaFrameworkControl(c, group.frameworkId, ctx);
      records.push(rec);
      for (const testId of c.testIds) relations.push({ parentKey: rec.recordKey, childKey: makeKey(ctx, 'vanta', 'test', testId), kind: 'control_test' });
      for (const docId of c.documentIds) relations.push({ parentKey: rec.recordKey, childKey: makeKey(ctx, 'vanta', 'document', docId), kind: 'control_document' });
    }
  }
  for (const v of payload.vulnerabilities ?? []) records.push(normalizeVantaVulnerability(v, ctx, warnings));
  for (const p of payload.people ?? []) {
    const rec = normalizeVantaPerson(p, ctx, warnings);
    records.push(rec);
    for (const computerId of p.computerIds) relations.push({ parentKey: rec.recordKey, childKey: makeKey(ctx, 'vanta', 'computer', computerId), kind: 'device_owner' });
  }
  for (const c of payload.monitoredComputers ?? []) {
    records.push(normalizeVantaComputer(c, ctx));
    if (c.ownerId) relations.push({ parentKey: makeKey(ctx, 'vanta', 'person', c.ownerId), childKey: makeKey(ctx, 'vanta', 'computer', c.id), kind: 'device_owner' });
  }
  for (const i of payload.integrations ?? []) records.push(normalizeVantaIntegration(i, ctx));
  for (const v of payload.vendors ?? []) records.push(normalizeVantaVendor(v, ctx, warnings));

  for (const r of records) if (r.parentKey) relations.push({ parentKey: r.parentKey, childKey: r.recordKey, kind: 'entity' });

  result.records = linkChildren(dedupeAndSort(records, warnings), warnings);
  result.relations = dedupeRelations(relations);
  const byKey = new Map(result.records.map((record) => [record.recordKey, record]));
  const controlChildren = new Map<RecordKey, StandardStatus[]>();
  for (const relation of result.relations) {
    if (relation.kind !== 'control_test' && relation.kind !== 'control_document') continue;
    const child = byKey.get(relation.childKey);
    if (child?.deactivated || child?.status === 'DEACTIVATED' || child?.status === 'NOT_APPLICABLE') continue;
    const children = controlChildren.get(relation.parentKey) ?? [];
    // Missing records are unknown evidence, not a reason to report a false pass.
    children.push(child?.status ?? 'UNKNOWN');
    controlChildren.set(relation.parentKey, children);
  }
  for (const control of result.records) {
    if (control.objectType !== 'control' || control.deactivated) continue;
    if (payload.controlLinksComplete === false) {
      control.status = 'UNKNOWN';
      control.rawStatus = 'Control evidence links incomplete';
      continue;
    }
    const children = controlChildren.get(control.recordKey) ?? [];
    control.status = deriveVantaControlStatus(children);
    control.rawStatus = `${children.filter((status) => status === 'PASS').length}/${children.length} tests+docs passing`;
  }
  return result;
}

export function dedupeRelations(relations: ParentChildRelation[]): ParentChildRelation[] {
  const seen = new Set<string>();
  const out: ParentChildRelation[] = [];
  for (const r of relations) {
    const k = `${r.kind}|${r.parentKey}|${r.childKey}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out.sort((a, b) => (a.parentKey < b.parentKey ? -1 : a.parentKey > b.parentKey ? 1 : a.childKey < b.childKey ? -1 : a.childKey > b.childKey ? 1 : 0));
}

/** Parse the retained raw payload of a Vanta record of the given type; null when it does not fit. */
export function vantaRaw<T>(record: SourceRecord, objectType: SourceRecord['objectType'], parse: (raw: unknown) => { success: true; data: T } | { success: false }): T | null {
  if (record.provider !== 'vanta' || record.objectType !== objectType) return null;
  const parsed = parse(record.raw);
  return parsed.success ? parsed.data : null;
}

export type { RecordKey as VantaRecordKey };
